import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'New Home Construction Electrical Services',
  provider: {
    '@type': 'ElectricalContractor',
    name: 'Electrician Solutions Fresno',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Fresno',
      addressRegion: 'CA',
      addressCountry: 'US',
    },
  },
  areaServed: {
    '@type': 'City',
    name: 'Fresno',
    '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
  },
  description:
    'Professional electrical installation services for new home construction in Fresno CA including complete home wiring, electrical panels, lighting, outlets, switches, smart home pre-wiring, and code compliance by licensed C-10 electricians',
  url: 'https://electriciansolutionsfresno.com/services/residential-electrical/new-home-electrical',
};

export default function NewHomeElectricalPage() {
  const features = [
    {
      icon: 'ri-home-line',
      title: 'Complete Home Wiring',
      description:
        'Full electrical wiring for new home construction — all circuits, outlets, switches, lighting, and appliance connections meeting California Title 24 codes for Fresno new builds',
    },
    {
      icon: 'ri-dashboard-line',
      title: 'Electrical Panel Installation',
      description:
        'Professional electrical panel installation sized for your new home — 200A+ service panels with room for future expansion and modern circuit protection for Fresno properties',
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Lighting Design & Installation',
      description:
        'Custom lighting design and installation for new homes — recessed lights, chandeliers, pendant lights, outdoor lighting, and landscape lighting for Fresno new construction',
    },
    {
      icon: 'ri-home-wifi-line',
      title: 'Smart Home Pre-Wiring',
      description:
        'Smart home infrastructure and pre-wiring for new builds — network cabling, smart switches, automated lighting, security systems, and home automation for Fresno homes',
    },
    {
      icon: 'ri-plug-line',
      title: 'Outlet & Switch Installation',
      description:
        'Strategic outlet and switch placement throughout your new home — USB charging outlets, GFCI protection, dimmer switches, and smart switch installations for Fresno new builds',
    },
    {
      icon: 'ri-flashlight-line',
      title: 'EV Charger Pre-Wiring',
      description:
        'Electric vehicle charging infrastructure for new homes — dedicated 240V circuits, conduit runs, and panel capacity for Level 2 EV chargers in Fresno new construction',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Code Compliance & Inspections',
      description:
        'Ensure all new home electrical work meets California Title 24 energy codes, NEC standards, and local Fresno building requirements — permits, inspections, and full compliance',
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Appliance & HVAC Electrical',
      description:
        'Complete electrical installations for all appliances and HVAC systems — dedicated circuits for ranges, dryers, AC units, furnaces, and pool equipment in Fresno new homes',
    },
  ];

  const whatsIncluded = [
    'Free new home electrical consultation and detailed project quote for Fresno properties',
    'Licensed C-10 electricians with extensive new construction experience',
    'Coordination with builders, architects, and homeowners throughout construction',
    'Complete electrical plans and layouts meeting California Title 24 building codes',
    'High-quality materials and equipment from trusted brands — Leviton, Lutron, Square D',
    'Permits pulled and inspections scheduled for all phases of electrical installation',
    'Smart home pre-wiring and infrastructure for future technology upgrades',
    'Warranty on all labor and materials — your new home electrical system is guaranteed',
  ];

  return (
    <>
      <SEOHead
        title="New Home Construction Electrical Fresno CA | New Build Electrician"
        description="Professional electrical services for new home construction in Fresno, CA. Complete home wiring, electrical panels, lighting, smart home pre-wiring, code compliance. Licensed C-10 electricians. Free quotes."
        canonical="/services/residential-electrical/new-home-electrical"
        keywords="new home electrician Fresno, new construction electrical Fresno CA, new build wiring Fresno, home electrical installation Fresno"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="New Home Construction Electrical — Fresno, CA"
        heroSubtitle="Complete electrical installation for your new home build — from rough-in to final trim"
        heroImage="https://public.readdy.ai/ai/img_res/2f36c0df1f730d0c5f68f542686dd88a.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Professional%20electrician%20installing%20new%20electrical%20panel%20in%20new%20construction%20home%20with%20organized%20circuit%20breakers%20and%20wiring%20tools%20and%20equipment%20visible%20bright%20indoor%20lighting%20California%20new%20build&width=700&height=600&seq=newbuild-fresno-panel-05&orientation=portrait"
        tagline="Trusted by 500+ Fresno Homeowners"
        fresnoContent="Powering new Fresno homes with professional electrical installation services that meet California building codes. Whether you're building a custom home in Woodward Park, developing a new property in Fig Garden, constructing a modern home in Clovis, or building in any Fresno area, our licensed C-10 electricians provide complete electrical services from rough-in to final trim. New home construction requires careful planning, coordination with builders and architects, and strict adherence to California Title 24 energy codes and local building requirements. We install electrical panels sized for modern demands, complete home wiring, smart home infrastructure, EV charger pre-wiring, custom lighting, and all electrical systems your new home needs."
        features={features}
        whatsIncluded={whatsIncluded}
        faqs={[
          {
            question: 'What electrical services are needed for new home construction in Fresno?',
            answer:
              'New home construction requires complete electrical installation including: temporary power service during construction, electrical panel installation (typically 200A+), rough-in wiring for all circuits, outlet and switch boxes, lighting rough-in, appliance circuits, HVAC electrical, smart home pre-wiring, EV charger infrastructure, final trim installation (outlets, switches, fixtures), and final inspections. Our licensed electricians coordinate with your builder throughout all construction phases.',
          },
          {
            question: 'How much does new home electrical installation cost in Fresno?',
            answer:
              'New home electrical costs typically range from $8,000–$15,000 for a standard 2,000 sq ft home, or approximately $4–$8 per square foot. Larger homes, custom features, smart home systems, and high-end finishes increase costs. We provide detailed quotes based on your new home plans and specifications.',
          },
          {
            question: 'Should I include smart home pre-wiring in my new Fresno home?',
            answer:
              "Absolutely. Smart home pre-wiring during construction is much easier and less expensive than retrofitting later. We recommend including: network cabling (Cat6) to key locations, smart switch wiring (neutral wires required), security system pre-wiring, whole-home audio/video infrastructure, outdoor camera locations, and smart thermostat wiring.",
          },
          {
            question: 'What size electrical panel do I need for my new Fresno home?',
            answer:
              'Most new Fresno homes require 200-amp electrical panels to handle modern electrical demands including HVAC systems, kitchen appliances, electric dryers, pool equipment, and EV chargers. Larger homes (3,000+ sq ft) or homes with high electrical loads may need 400-amp service. We assess your home plans and future needs to recommend the appropriate panel size.',
          },
          {
            question: 'Do you coordinate with builders during new home construction?',
            answer:
              'Yes, coordination with builders is essential for successful new home electrical installation. We work closely with general contractors, framers, HVAC contractors, and other trades throughout construction. Electrical work must be sequenced properly — rough-in after framing, coordination with insulation and drywall, and final trim after painting.',
          },
          {
            question: 'What inspections are required for new home electrical in Fresno?',
            answer:
              'New home electrical work in Fresno requires multiple inspections: temporary power inspection, rough-in inspection (after wiring is installed but before drywall), and final inspection (after all fixtures and devices are installed). We handle all permit applications, schedule required inspections, and ensure your electrical installation passes all inspections and meets California Title 24 codes.',
          },
        ]}
        serviceSlug="new-home-electrical"
      />
    </>
  );
}
